'use client';

import { useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check, Sparkles, FileText, Upload, X, Download, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsonToTsvContent } from '../../data/json-to-tsv-content';
import { cn } from '@/lib/utils';

const sampleJson = `[
  {
    "name": "John Doe",
    "age": 30,
    "city": "New York",
    "isActive": true,
    "skills": ["JavaScript", "TypeScript", "React"]
  },
  {
    "name": "Jane Smith",
    "age": 25,
    "city": "San Francisco",
    "isActive": false,
    "skills": ["Python", "Data Analysis"]
  },
  {
    "name": "Bob Johnson",
    "age": 35,
    "city": "Chicago",
    "isActive": true,
    "skills": ["Java", "Spring", "SQL"]
  }
]`;

const sampleTsv = `name\tage\tcity\tisActive\tskills
John Doe\t30\tNew York\ttrue\t"JavaScript, TypeScript, React"
Jane Smith\t25\tSan Francisco\tfalse\t"Python, Data Analysis"
Bob Johnson\t35\tChicago\ttrue\t"Java, Spring, SQL"`;

function convertJsonToTsv(jsonString: string): string {
    try {
        if (!jsonString.trim()) {
            return '';
        }

        // Parse JSON string to JavaScript object/array
        let jsonData;
        try {
            jsonData = JSON.parse(jsonString);
        } catch (e) {
            throw new Error('Invalid JSON format');
        }

        // If it's not an array, wrap it in an array
        const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];

        // Convert to TSV using PapaParse
        return Papa.unparse(dataArray, {
            delimiter: '\t',
            header: true,
            quotes: true,
            skipEmptyLines: true,
        });
    } catch (error) {
        throw new Error(`Error converting JSON to TSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

export default function JsonToTsvTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isPretty, setIsPretty] = useState(true);
    const [fileName, setFileName] = useState<string | null>(null);
    const [isConverting, setIsConverting] = useState(false);
    const { toast } = useToast();

    const convertJson = useCallback((jsonString: string) => {
        try {
            if (!jsonString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            setIsConverting(true);
            const tsvString = convertJsonToTsv(jsonString);
            setOutput(tsvString);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid JSON'}`);
            setOutput('');
        } finally {
            setIsConverting(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            convertJson(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input, convertJson]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);

        // Reset the file input
        e.target.value = '';
    };

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The TSV has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownload = () => {
        if (!output) return;

        const blob = new Blob([output], { type: 'text/tab-separated-values;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName ? fileName.replace(/\.\w+$/, '.tsv') : 'converted_data.tsv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
        setFileName(null);
    };

    const loadSample = () => {
        setInput(sampleJson);
        setFileName('sample.json');
        setIsPretty(true);
    };

    const togglePretty = () => {
        if (!input) return;

        try {
            const json = JSON.parse(input);
            setInput(isPretty
                ? JSON.stringify(json, null, 2)
                : JSON.stringify(json)
            );
            setIsPretty(!isPretty);
        } catch (e) {
            // If not valid JSON, don't toggle
        }
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="JSON to TSV Converter">
                Convert JSON data to TSV (Tab-Separated Values) format. Free online JSON to TSV converter.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto mb-6">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                            Upload JSON File
                        </label>
                        <label className="flex items-center justify-center w-full px-4 py-2 bg-background border border-dashed border-input rounded-md cursor-pointer hover:bg-accent/50 transition-colors">
                            <Upload className="h-4 w-4 mr-2" />
                            <span className="text-sm truncate max-w-[200px]">
                                {fileName || 'Choose a file...'}
                            </span>
                            <input
                                type="file"
                                accept=".json,application/json"
                                className="hidden"
                                onChange={handleFileUpload}
                                disabled={isConverting}
                            />
                        </label>
                        {fileName && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setFileName(null);
                                    setInput('');
                                }}
                                className="mt-1 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                disabled={isConverting}
                            >
                                <X className="h-3 w-3" />
                                Remove file
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 mt-4 sm:mt-0">
                        <Button
                            variant={isPretty ? "default" : "outline"}
                            size="sm"
                            onClick={togglePretty}
                            disabled={!input || isConverting}
                            className={isPretty ? "bg-primary/10 hover:bg-primary/20" : ""}
                        >
                            <span className="text-xs">Pretty</span>
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadSample}
                            className="gap-1.5"
                            disabled={isConverting}
                        >
                            <Sparkles className="h-3.5 w-3.5" />
                            Sample
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClear}
                            disabled={!input || isConverting}
                            className="text-muted-foreground hover:text-foreground"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        JSON Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName || 'Paste your JSON here'}
                                    </CardDescription>
                                </div>
                                <div className="text-sm font-mono bg-muted/50 px-2 py-1 rounded">
                                    JSON
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your JSON here...\n' +
                                        '[\n' +
                                        '  {\n' +
                                        '    \"name\": \"John\",\n' +
                                        '    \"age\": 30,\n' +
                                        '    \"city\": \"New York\"\n' +
                                        '  }\n' +
                                        ']'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                    disabled={isConverting}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-4 lg:my-0">
                    <div className="flex flex-col items-center">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            <ArrowRight className="h-5 w-5" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                            Convert to
                        </p>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        TSV Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {fileName ?
                                            fileName.replace(/\.\w+$/, '.tsv') :
                                            'Your TSV will appear here'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        disabled={!output || isConverting}
                                        className="gap-1.5"
                                    >
                                        <Download className="h-3.5 w-3.5" />
                                        Download
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        disabled={!output || isConverting}
                                        className={cn(
                                            'gap-1.5 transition-all',
                                            isCopied
                                                ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                                : 'hover:bg-primary/5'
                                        )}
                                    >
                                        {isCopied ? (
                                            <Check className="h-4 w-4" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                        {isCopied ? 'Copied!' : 'Copy'}
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70',
                                        'whitespace-pre' // Preserve formatting
                                    )}
                                    placeholder="Your TSV will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your TSV will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                                {isConverting && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={jsonToTsvContent.title}
                    description={jsonToTsvContent.description}
                    content={jsonToTsvContent.content}
                    faqs={jsonToTsvContent.faqs}
                    relatedTools={jsonToTsvContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
